/**
 * External dependencies
 */
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import Breadcrumbs from '@Components/Breadcrumbs';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import SettingDisplay from '@Components/SettingDisplay';
import ShowFor from '@Components/ShowFor';
import { Space } from '@Types/venue';
import { buildFilterParams } from '@Utils/utils';
import { SquareDashed } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSpaces } from '../../hooks/useSpaces';
import VenueQuickFilter from '@Components/CustomDataTableFilters/VenueQuickFilter';

const venueColumnConfigs: ColumnConfig<Space>[] = [
  {
    id: 'name',
    headerLabel: 'Space Name',
    accessor: (row) => row.name ?? '-',
    type: 'text',
  },
  {
    id: 'disabledAt',
    headerLabel: 'Status',
    cell: (row) => {
      const rowData = row.row.original;
      return rowData.disabledAt ? (
        <Badge variant="destructive">Disabled</Badge>
      ) : (
        <Badge variant="outline">Enabled</Badge>
      );
    },
    type: 'text',
    customSortingFn: (rowA, rowB) => {
      const a = rowA.original.disabledAt ? 1 : 0;
      const b = rowB.original.disabledAt ? 1 : 0;
      return a - b;
    },
  },
  {
    id: 'venueId',
    headerLabel: 'Venue',
    accessor: (row) => row.venue?.name ?? '-',
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Link to={`/venues/${row.venue?.id}`} className="hover:underline">
            {row.venue?.name ?? '-'}
          </Link>
        </div>
      );
    },
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
  {
    id: 'tripleSeatSettings',
    header: () => (
      <div className="flex items-center gap-1">
        <img src="/triple-seat-favicon-400x400.png" className="w-4" />
        <span>Triple Seat Settings</span>
      </div>
    ),
    cell: (info) => {
      const row = info.row.original;
      return <SettingDisplay id={row.tripleSeatId} label="Area" />;
    },
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
];

export default function Spaces() {
  const navigate = useNavigate();

  const controller = useResourceTableController<Space>({
    defaultSort: {
      id: 'name',
    },
    columnsConfig: venueColumnConfigs,
    baseSearchParams: () => ({
      'include': 'venue',
      'filter[venue.deletedAt]': 'null',
    }),
    buildFilterParams: (filters) => buildFilterParams(filters),
    quickFilterFields: [{ field: 'name', label: 'Space Name' }],
    initialPageSize: 10,
  });

  const { data: payload, status } = useSpaces(controller.searchParams);
  const { data: spacesMeta, status: spacesMetaStatus } = useSpaces();

  const rows: Space[] = Array.isArray(payload) ? payload : [];

  const meta = useMemo(() => {
    if (spacesMetaStatus !== 'success') return undefined;
    if (!Array.isArray(spacesMeta)) return undefined;
    return {
      total: Array.isArray(spacesMeta) ? spacesMeta.length : 0,
    };
  }, [spacesMeta, spacesMetaStatus]);

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Locations', href: '/locations' },
          { name: 'Spaces', href: '/spaces' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Spaces</Heading>
          <p>Manage your spaces.</p>
        </div>
        <div className="flex justify-end">
          <ShowFor permissions={['SPACES_WRITE']}>
            <Button asChild>
              <Link to="/spaces/create">Create a space</Link>
            </Button>
          </ShowFor>
        </div>
      </div>
      <Card>
        <CardContent>
          <ResourceTableView<Space>
            label="Spaces"
            controller={controller}
            data={rows}
            meta={meta}
            status={status}
            summaryIcon={<SquareDashed className="h-4 w-4 text-zinc-500" />}
            summaryLabel="Total Spaces: "
            onRowClick={(row) => navigate(`/spaces/${row.id}`)}
            renderQuickFiltersExtra={({ table }) => (
              <VenueQuickFilter<Space> table={table} />
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
